import React from "react"
import { getDevice } from "@src/api"
import { SLIM_DEVICE_TYPE } from "@src/devices/types"
import { DeviceFilters } from "./types"

export const toCsv = (rows: Array<Record<string, unknown>>, headersOverride?: string[]): string => {
  const escape = (val: any) => {
    const s = val === null || val === undefined ? "" : String(val)
    const needsQuote = /[",\n]/.test(s)
    const escaped = s.replace(/"/g, '""')
    return needsQuote ? `"${escaped}"` : escaped
  }

  if (rows.length === 0) return ""

  const headerSet = new Set<string>()
  rows.forEach((r) => Object.keys(r).forEach((k) => headerSet.add(k)))

  const headers =
    headersOverride && headersOverride.length > 0 ? headersOverride : Array.from(headerSet).sort()

  const headerLine = headers.map(escape).join(",")
  const lines = rows.map((row) => headers.map((h) => escape(row[h])).join(","))

  return [headerLine, ...lines].join("\n")
}

export const numberOrEmpty = (value: unknown): number | "" => {
  if (typeof value === "number" && !Number.isNaN(value)) return value
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    return Number(value)
  }
  return ""
}

export const flattenObject = (
  input: unknown,
  parentKey = "",
  result: Record<string, unknown> = {},
): Record<string, unknown> => {
  if (input === null || input === undefined) {
    if (parentKey) result[parentKey] = ""
    return result
  }

  const isPlainObject = (val: unknown) => Object.prototype.toString.call(val) === "[object Object]"

  if (Array.isArray(input)) {
    // Keep arrays intact as JSON to avoid exploding columns
    if (parentKey) result[parentKey] = JSON.stringify(input)
    return result
  }

  if (isPlainObject(input)) {
    Object.entries(input as Record<string, unknown>).forEach(([key, value]) => {
      const newKey = parentKey ? `${parentKey}.${key}` : key
      if (value !== null && typeof value === "object") {
        if (Array.isArray(value)) {
          result[newKey] = JSON.stringify(value)
        } else {
          flattenObject(value, newKey, result)
        }
      } else {
        result[newKey] = value
      }
    })
    return result
  }

  if (parentKey) result[parentKey] = input
  return result
}

export const exportCsv = async (
  partnerUuid: string,
  fleetUuid: string | undefined,
  sorting: boolean,
  sortHeader: string,
  filters: DeviceFilters,
  cancelledRef?: React.MutableRefObject<boolean>,
): Promise<void> => {
  if (!partnerUuid) return

  const perPage = 500

  // Fetch first page
  const first = await getDevice(partnerUuid, fleetUuid, {
    page: 0,
    per_page: perPage,
    sort_desc: sorting,
    sort_by: sortHeader,
    ...filters,
  })

  if (!first.response.ok) {
    return
  }

  // Check if cancelled after first fetch
  if (cancelledRef?.current) {
    return
  }

  const allDevices: SLIM_DEVICE_TYPE[] = [...first.data.devices]
  const total = first.data.meta.total
  const perPageFromApi = first.data.meta.per_page || perPage
  const totalPages = Math.ceil(total / perPageFromApi)

  // Fetch remaining pages
  for (let page = 1; page < totalPages; page++) {
    // Check if cancelled before each fetch
    if (cancelledRef?.current) {
      return
    }

    const { response, data } = await getDevice(partnerUuid, fleetUuid, {
      page,
      per_page: perPage,
      sort_desc: sorting,
      sort_by: sortHeader,
      ...filters,
    })

    // Check if cancelled after each fetch
    if (cancelledRef?.current) {
      return
    }

    if (response.ok) {
      allDevices.push(...data.devices)
    } else {
      break
    }
  }

  // Check if cancelled before processing data
  if (cancelledRef?.current) {
    return
  }

  // Build rows with required columns in fixed order and adapted names
  const rows = allDevices.map((d) => ({
    serial_number: d.serialnumber ?? "",
    tracker_name: d.name ?? "",
    fleet: d.fleet?.name ?? "",
    battery: d.battery ?? "",
    locked: d.locked ?? "",
    powersaving_ends_at: d.powersaving_ends_at ?? "",
    continous_servicebookings_until:
      (d as SLIM_DEVICE_TYPE & { continuous_servicebookings_until?: string })
        .continuous_servicebookings_until ??
      (d as SLIM_DEVICE_TYPE & { continous_servicebookings_until?: string })
        .continous_servicebookings_until ??
      d.servicebooking?.service_end_date ??
      "",
    "position.accuracy": numberOrEmpty((d.position as { accuracy?: number })?.accuracy),
    "position.latitude": d.position?.lat ?? (d.position as { latitude?: string })?.latitude ?? "",
    "position.longitude":
      d.position?.lng ?? (d.position as { longitude?: string })?.longitude ?? "",
    last_position_timestamp: d.last_position_timestamp ?? "",
    reported_theft: d.reported_theft != null ? 1 : 0,
    last_seen_timestamp: d.last_seen_timestamp ?? "",
    id_number: d.id_number ?? "",
  }))

  // Check if cancelled after mapping rows
  if (cancelledRef?.current) {
    return
  }

  const headers = [
    "serial_number",
    "id_number",
    "tracker_name",
    "fleet",
    "battery",
    "locked",
    "powersaving_ends_at",
    "continous_servicebookings_until",
    "position.accuracy",
    "position.latitude",
    "position.longitude",
    "last_position_timestamp",
    "reported_theft",
    "last_seen_timestamp",
  ]

  // Check if cancelled before creating CSV
  if (cancelledRef?.current) {
    return
  }

  const csv = toCsv(rows, headers)

  // Check if cancelled before creating blob
  if (cancelledRef?.current) {
    return
  }

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)

  // Check if cancelled before downloading
  if (cancelledRef?.current) {
    URL.revokeObjectURL(url)
    return
  }

  const a = document.createElement("a")
  const ts = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  const filename = `trackers_${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(
    ts.getHours(),
  )}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.csv`
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
